import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Badge,
  Spinner,
  Card,
  Row,
  Col,
  ListGroup,
} from "react-bootstrap";
import {
  FaEdit,
  FaPlus,
  FaSave,
  FaTrash,
  FaLink,
  FaEnvelope,
  FaMapMarkerAlt,
  FaFolderOpen,
} from "react-icons/fa";

const API_URL = "http://radheyelectical.atwebpages.com/api/api_handler.php";

const AdminDashboard = () => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("");

  const initialFormState = {
    id: "",
    action: "insert",
    category: "",
    badge: "",
    title: "",
    subtitle: "",
    description: "",
    image_url: "",
    icon_name: "",
    contact_email: "",
    extra_info: "",
    sort_order: 0,
    map_url: "",
  };
  const [formData, setFormData] = useState(initialFormState);

  // --- 1. FETCH DATA (Fixed with useCallback) ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(API_URL);
      const result = await response.json();
      if (result.status === "success") {
        setData(result.data);
        const categories = Object.keys(result.data);
        if (categories.length > 0 && !activeTab) {
          setActiveTab(categories[0]);
        }
      }
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- 2. SAVE DATA ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const result = await response.json();
      if (result.status === "success") {
        setShowModal(false);
        fetchData();
      } else {
        alert("Server Error: " + result.message);
      }
    } catch (error) {
      alert("Network error occurred.");
    }
  };

  // --- 3. DELETE DATA ---
  const handleDelete = async (id) => {
    if (window.confirm("Permanent Delete? This action cannot be undone.")) {
      try {
        const response = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "delete", id: id }),
        });
        const result = await response.json();
        if (result.status === "success") fetchData();
      } catch (error) {
        alert("Network error.");
      }
    }
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setFormData({ ...item, action: "update" });
    } else {
      setFormData({
        ...initialFormState,
        category: activeTab,
        action: "insert",
      });
    }
    setShowModal(true);
  };

  if (loading && !Object.keys(data).length)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <Spinner animation="border" variant="primary" />
      </div>
    );

  return (
    <Container fluid className="">
      <Row>
        {/* --- CATEGORY NAVIGATION (ListGroup) --- */}
        <Col md={4} xl={3} lg={3} className="mb-4">
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-dark text-white fw-bold py-3">
              <FaFolderOpen className="me-2" /> Categories
            </Card.Header>
            <ListGroup variant="flush">
              {Object.keys(data).map((category) => (
                <ListGroup.Item
                  key={category}
                  action
                  active={activeTab === category}
                  onClick={() => setActiveTab(category)}
                  className="d-flex justify-content-between align-items-center py-3 border-start-0 border-end-0"
                  style={{ cursor: "pointer", fontSize: "0.9rem" }}
                >
                  <span className="text-uppercase fw-bold">{category}</span>
                  <Badge
                    bg={activeTab === category ? "light" : "primary"}
                    text={activeTab === category ? "dark" : "white"}
                    pill
                  >
                    {data[category].length}
                  </Badge>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Card>
        </Col>

        {/* --- MAIN CONTENT AREA --- */}
        <Col md={8} xl={9} lg={9}>
          <Card className="shadow-sm border-0 rounded-3">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center py-3">
              <h5 className="mb-0 text-dark fw-bold text-uppercase letter-spacing-1">
                {activeTab} Content
              </h5>
              <Button
                variant="success"
                size="sm"
                onClick={() => handleOpenModal()}
              >
                <FaPlus className="me-2" /> Add New Entry
              </Button>
            </Card.Header>

            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th className="ps-4" style={{ width: "80px" }}>
                      Order
                    </th>
                    <th>Content Details</th>
                    <th>Identifiers</th>
                    <th className="text-end pe-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeTab &&
                    data[activeTab]?.map((item) => (
                      <tr key={item.id}>
                        <td className="ps-4 font-monospace text-muted">
                          {item.sort_order}
                        </td>
                        <td>
                          <div className="fw-bold text-primary">
                            {item.title}
                          </div>
                          <div className="text-muted small">
                            {item.subtitle}
                          </div>
                          <p
                            className="small text-secondary mb-0"
                            style={{ fontSize: "0.8rem" }}
                          >
                            {item.description
                              ? item.description.substring(0, 60) + "..."
                              : ""}
                          </p>
                        </td>
                        <td>
                          {item.badge && (
                            <Badge
                              bg="warning"
                              text="dark"
                              className="d-block mb-1"
                            >
                              {item.badge}
                            </Badge>
                          )}
                          <small className="text-muted d-block">
                            <FaLink className="me-1" />{" "}
                            {item.icon_name || "No Icon"}
                          </small>
                        </td>
                        <td className="text-end pe-4">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => handleOpenModal(item)}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(item.id)}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* --- ORIGINAL FULL MODAL MODEL --- */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
        backdrop="static"
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton className="bg-light">
            <Modal.Title className="text-primary h5">
              {formData.action === "update" ? "Modify" : "Create"}{" "}
              {formData.category} Record
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-4">
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold small">Title</Form.Label>
                  <Form.Control
                    required
                    placeholder="Main heading..."
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold small">Category</Form.Label>
                  <Form.Control
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold small">
                    Subtitle / Label
                  </Form.Label>
                  <Form.Control
                    value={formData.subtitle || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, subtitle: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold small">Badge Text</Form.Label>
                  <Form.Control
                    placeholder="e.g. Certified"
                    value={formData.badge || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, badge: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-bold small">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.description || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold small">
                    Image / Destination URL
                  </Form.Label>
                  <Form.Control
                    value={formData.image_url || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, image_url: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold small">
                    <FaEnvelope className="me-1" /> Contact Email
                  </Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.contact_email || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        contact_email: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold small">
                    Icon (FontAwesome)
                  </Form.Label>
                  <Form.Control
                    value={formData.icon_name || ""}
                    placeholder="e.g. FaBolt"
                    onChange={(e) =>
                      setFormData({ ...formData, icon_name: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold small">
                    Sorting Order
                  </Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) =>
                      setFormData({ ...formData, sort_order: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold small">
                    <FaMapMarkerAlt className="me-1" /> Map URL
                  </Form.Label>
                  <Form.Control
                    value={formData.map_url || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, map_url: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="bg-light">
            <Button
              variant="outline-secondary"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" className="px-4">
              <FaSave className="me-2" /> Save Changes
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default AdminDashboard;
